// ===================================================
// ФАЙЛ: frontend/src/components/GlobalSearch/GlobalSearch.jsx
// Глобальный поиск по платформе: товары, поставщики, (будущее: заказы)
// Открывается: клик по строке Header; плюс по режиму —
//   hotkey → Ctrl/Cmd+K
//   type → начало ввода / сканер (когда ни одно поле не в фокусе)
// Режим: utils/globalSearchActivation.js (переключатель в Header)
// ===================================================
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import {
  Search,
  ScanBarcode,
  Plus,
  Store,
  ShoppingCart,
  Inbox,
  X,
  ArrowRight,
  CheckCircle,
  Settings,
  History,
  Grid3x3,
  AlertCircle,
  Tag as TagIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'utils/axios';
import { getGlobalSearchApiUrl } from 'utils/constants';
import { buildGlobalSearchScopeKey, globalSearchRecentStorageKey, createGlobalSearchLifecycle, runGlobalSearchWaves } from './globalSearchLifecycle';
import { useAuth } from '../../hooks/useAuth';
import { trackSearch } from '../../services/analyticsClient';
import { pickSafeRussianBackendMessage, pickHttpStatusRussianFallback } from '../../utils/safeRussianBackendMessage';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from 'utils/constants';
import {
  GLOBAL_SEARCH_ACTIVATION,
  GLOBAL_SEARCH_ACTIVATION_EVENT,
  getGlobalSearchActivationMode,
} from '../../utils/globalSearchActivation';
/* Стили: STYLESYSTEM/components/global-search.css (см. index.css @import) */

// Search does not receive Hub audience prices or FAS quantities with sale UOM.
// Legacy price/stock scalars cannot establish either fact, including zero.
function ProductCommerceLink({ name, onClick }) {
  return (
    <div className="gs-result-category">
      <span>Цена и наличие — </span>
      <button
        type="button"
        className="mb-btn mb-btn--link"
        aria-label={`Открыть карточку товара${name ? ` «${name}»` : ''}`}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        в карточке товара
      </button>
    </div>
  );
}

function pickProductImage(product) {
  if (!product || typeof product !== 'object') return null;
  const direct =
    product.image_url ||
    product.main_image_url ||
    product.thumbnail_url ||
    product.thumb_url ||
    product.photo_url ||
    null;
  if (direct) return String(direct);

  const images = Array.isArray(product.images) ? product.images : [];
  const first = images[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object') {
    return first.proxy_url || first.url || first.image_url || first.src || null;
  }
  return null;
}

// ──────────────────────────────────────────────
// Подсветка совпадений — как в списке «Товары» (маркерная плашка)
// ──────────────────────────────────────────────
function escapeRegExp(s) {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightMatch(text, query) {
  if (text == null || text === '') return text;
  const needles = String(query || '')
    .trim()
    .split(/\s+/g)
    .map((s) => s.trim())
    .filter((s) => s.length >= 2)
    .slice(0, 8);
  if (needles.length === 0) return text;
  try {
    const source = String(text);
    const re = new RegExp(needles.map(escapeRegExp).join('|'), 'ig');
    const nodes = [];
    let lastIndex = 0;
    let match;
    let idx = 0;
    while ((match = re.exec(source)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(<span key={`t-${idx++}`}>{source.slice(lastIndex, match.index)}</span>);
      }
      nodes.push(
        <mark key={`m-${idx++}`} className="gs-highlight">{match[0]}</mark>
      );
      lastIndex = match.index + match[0].length;
    }
    if (nodes.length === 0) return text;
    if (lastIndex < source.length) {
      nodes.push(<span key={`t-${idx++}`}>{source.slice(lastIndex)}</span>);
    }
    return nodes;
  } catch {
    return text;
  }
}

// ══════════════════════════════════════════════
// КОМПОНЕНТ GlobalSearch
// ══════════════════════════════════════════════
// Stable component types preserve focus as later search waves update their props.
function OurProductItem({ product, isSelected, onSelect, onClick, searchQuery }) {
  const imageUrl = pickProductImage(product);
  const q = searchQuery || '';

  return (
    <div
      className={`gs-result-item gs-result-our ${isSelected ? 'gs-result-selected' : ''}`}
      onClick={onClick}
      onMouseEnter={onSelect}
    >
      <div className="gs-result-image">
        {imageUrl ? (
          <img src={imageUrl} alt="" loading="lazy" />
        ) : (
          <Inbox className="gs-result-image-placeholder" size={24} aria-hidden />
        )}
      </div>
      <div className="gs-result-info">
        <div className="gs-result-name">{highlightMatch(product.name, q)}</div>
        <div className="gs-result-meta">
          {product.public_id != null && product.public_id !== '' && (
            <span className="gs-result-sku">
              ID: {highlightMatch(String(product.public_id).padStart(7, '0'), q)}
            </span>
          )}
          {(product.sku || product.article) && (
            <span className="gs-result-sku">арт: {highlightMatch(product.sku || product.article, q)}</span>
          )}
          {product.brand_name && (
            <span className="mb-badge mb-badge--info gs-result-brand-badge">{product.brand_name}</span>
          )}
          {product.category_name && (
            <span className="gs-result-category">{product.category_name}</span>
          )}
        </div>
        <ProductCommerceLink name={product.name} onClick={onClick} />
      </div>
    </div>
  );
}

function SupplierProductItem({ product, isSelected, onSelect, onClick, searchQuery }) {
  const imageUrl = pickProductImage(product);
  const q = searchQuery || '';

  return (
    <div
      className={`gs-supplier-card ${isSelected ? 'gs-result-selected' : ''}`}
      onClick={onClick}
      onMouseEnter={onSelect}
    >
      <div className="gs-supplier-card__image">
        {imageUrl ? (
          <img src={imageUrl} alt="" loading="lazy" />
        ) : (
          <Store className="gs-supplier-card__image-placeholder" size={24} aria-hidden />
        )}
        {product.is_imported && (
          <span className="gs-supplier-card__imported" title="Импортирован"><CheckCircle size={16} aria-hidden /></span>
        )}
      </div>
      <div className="gs-supplier-card__body">
        <div className="gs-supplier-card__name" title={product.name}>{highlightMatch(product.name, q)}</div>
        <div className="gs-supplier-card__codes">
          {(product.external_code || product.external_id) && (
            <span>ID: {highlightMatch(String(product.external_code || product.external_id), q)}</span>
          )}
          {product.vendor_code && <span>Арт: {highlightMatch(product.vendor_code, q)}</span>}
          {product.brand && <span className="gs-supplier-card__brand">{product.brand}</span>}
        </div>
        <ProductCommerceLink name={product.name} onClick={onClick} />
      </div>
    </div>
  );
}

const GLOBAL_SEARCH_EVENT = 'GLOBAL_SEARCH_OPEN';

export { GLOBAL_SEARCH_EVENT };

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { company, user } = useAuth();
  const scopeKey = buildGlobalSearchScopeKey(company?.id, user?.id);
  const recentStorageKey = globalSearchRecentStorageKey(company?.id, user?.id);
  const { hasPermission } = usePermissions();
  const canOpenAdminSearchSettings = hasPermission(PERMISSIONS.ADMIN_ACCESS);
  const inputRef = useRef(null);
  const searchLifecycleRef = useRef(null);
  if (!searchLifecycleRef.current) searchLifecycleRef.current = createGlobalSearchLifecycle();
  const searchLifecycle = searchLifecycleRef.current;
  useEffect(() => () => searchLifecycle.cancel(), [searchLifecycle]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchingMore, setSearchingMore] = useState(false);
  const [searchingMoreLabel, setSearchingMoreLabel] = useState('');
  const [results, setResults] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);
  const [searchTime, setSearchTime] = useState(null); // Время выполнения поиска (ms)
  const [searchError, setSearchError] = useState(null);
  const [activationMode, setActivationMode] = useState(() => getGlobalSearchActivationMode());
  const activationModeRef = useRef(activationMode);

  // Для сканера — накопление быстрого ввода
  const scanBufferRef = useRef([]);
  const scanTimestampsRef = useRef([]);
  const scanTimeoutRef = useRef(null);
  // Флаг для предотвращения потери символов при открытии окна
  const isOpeningRef = useRef(false);

  useEffect(() => {
    activationModeRef.current = activationMode;
  }, [activationMode]);

  useEffect(() => {
    const onActivationChange = (e) => {
      const next = e?.detail?.mode;
      if (next === GLOBAL_SEARCH_ACTIVATION.HOTKEY || next === GLOBAL_SEARCH_ACTIVATION.TYPE) {
        setActivationMode(next);
      } else {
        setActivationMode(getGlobalSearchActivationMode());
      }
    };
    window.addEventListener(GLOBAL_SEARCH_ACTIVATION_EVENT, onActivationChange);
    return () => window.removeEventListener(GLOBAL_SEARCH_ACTIVATION_EVENT, onActivationChange);
  }, []);

  // ────────────────────────────
  // Results and local history belong to the current authenticated company/user.
  // Reset before paint so a scope switch never displays the previous company's rows.
  useLayoutEffect(() => {
    searchLifecycle.syncScope(scopeKey);
    setOpen(false);
    setQuery('');
    setResults(null);
    setRecentSearches([]);
    setSearchError(null);
    setSearchTime(null);
    setSelectedIndex(0);
    setLoading(false);
    setSearchingMore(false);
    setSearchingMoreLabel('');
    scanBufferRef.current = [];
    scanTimestampsRef.current = [];
    isOpeningRef.current = false;
    if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
    try {
      // The previous unscoped key has no verified owner and is never imported.
      const saved = recentStorageKey ? localStorage.getItem(recentStorageKey) : null;
      const parsed = saved ? JSON.parse(saved) : [];
      if (Array.isArray(parsed)) setRecentSearches(parsed.filter((item) => typeof item === 'string').slice(0, 8));
    } catch (_) {
      // Storage failure must not turn a completed search into a search failure.
    }
  }, [scopeKey, recentStorageKey, searchLifecycle]);

  const addToRecent = useCallback((q) => {
    if (!recentStorageKey || !searchLifecycle.isScopeCurrent(scopeKey)) return;
    setRecentSearches((prev) => {
      if (!searchLifecycle.isScopeCurrent(scopeKey)) return prev;
      const updated = [q, ...prev.filter((item) => item !== q)].slice(0, 8);
      try { localStorage.setItem(recentStorageKey, JSON.stringify(updated)); } catch (_) { /* history is optional */ }
      return updated;
    });
  }, [recentStorageKey, scopeKey, searchLifecycle]);

  // ────────────────────────────
  // Поиск — определяем ДО handleOpen (зависимость)
  // ────────────────────────────
  const searchTimeoutRef = useRef(null);

  const performSearch = useCallback(async (searchQuery) => {
    const request = searchLifecycle.begin(searchQuery);
    if (request.query.length < 2) {
      searchLifecycle.cancel();
      setResults(null);
      setLoading(false);
      setSearchingMore(false);
      setSearchingMoreLabel('');
      return;
    }

    setResults(null);
    setSelectedIndex(0);

    setLoading(true);
    setSearchingMore(false);
    setSearchingMoreLabel('');
    setSearchTime(null);
    setSearchError(null);
    const t0 = performance.now();
    const q = request.query;

    const emptyPayload = () => ({
      our_products: [],
      supplier_products: [],
      categories: [],
      brands: [],
      orders: [],
      documents: [],
      warehouses: [],
      result_groups: [],
      suppliers_map: {},
      total_our: 0,
      total_supplier: 0,
      total_categories: 0,
      total_brands: 0,
      total_all: 0,
      query: q,
      search_incomplete: false,
    });

    const sortByRelevance = (rows) => {
      const list = Array.isArray(rows) ? [...rows] : [];
      list.sort((a, b) => {
        const sa = Number(a?.relevance_score) || 0;
        const sb = Number(b?.relevance_score) || 0;
        if (sb !== sa) return sb - sa;
        return String(a?.name || a?.title || '').localeCompare(String(b?.name || b?.title || ''), 'ru');
      });
      return list;
    };

    const mergeUniqueById = (prev, next) => {
      const map = new Map();
      for (const row of [...(prev || []), ...(next || [])]) {
        if (!row) continue;
        const key = String(row.id ?? `${row.supplier_id || ''}:${row.external_id || row.sku || row.name}`);
        const prevRow = map.get(key);
        if (!prevRow || (Number(row.relevance_score) || 0) >= (Number(prevRow.relevance_score) || 0)) {
          map.set(key, row);
        }
      }
      return sortByRelevance([...map.values()]);
    };

    const buildPayload = (payload, { incomplete } = {}) => {
      const our = sortByRelevance(payload.our_products || []);
      const supplier = sortByRelevance(payload.supplier_products || []);
      const categories = sortByRelevance(payload.categories || []);
      const brands = sortByRelevance(payload.brands || []);
      const orders = payload.orders || [];
      const documents = payload.documents || [];
      const warehouses = payload.warehouses || [];
      return {
        ...payload,
        our_products: our,
        supplier_products: supplier,
        categories,
        brands,
        orders,
        documents,
        warehouses,
        total_our: our.length,
        total_supplier: supplier.length,
        total_categories: categories.length,
        total_brands: brands.length,
        total_all:
          our.length
          + supplier.length
          + categories.length
          + brands.length
          + orders.length
          + documents.length
          + warehouses.length,
        search_incomplete: incomplete === true,
      };
    };

    const mergeWave = (waveData) => {
      if (!request.isCurrent()) return;
      setLoading(false);
      setResults((prev) => {
        if (!request.isCurrent()) return prev;
        const base = prev?.query === q ? prev : emptyPayload();
        return buildPayload({
          ...base,
          query: q,
          our_products: mergeUniqueById(base.our_products, waveData.our_products),
          supplier_products: mergeUniqueById(base.supplier_products, waveData.supplier_products),
          categories: mergeUniqueById(base.categories, waveData.categories),
          brands: mergeUniqueById(base.brands, waveData.brands),
          orders: mergeUniqueById(base.orders, waveData.orders),
          documents: mergeUniqueById(base.documents, waveData.documents),
          warehouses: mergeUniqueById(base.warehouses, waveData.warehouses),
          suppliers_map: { ...(base.suppliers_map || {}), ...(waveData.suppliers_map || {}) },
          is_barcode: waveData.is_barcode ?? base.is_barcode,
          suggested_actions: waveData.suggested_actions || base.suggested_actions,
          result_groups: (waveData.result_groups?.length
            ? waveData.result_groups
            : base.result_groups) || [],
        }, { incomplete: true });
      });
      setSearchTime(Math.round(performance.now() - t0));
    };

    try {
      setSearchingMore(true);
      const outcome = await runGlobalSearchWaves({
        request,
        get: (url, config) => axios.get(url, config),
        url: getGlobalSearchApiUrl(),
        onWave: mergeWave,
        onProgress: setSearchingMoreLabel,
      });
      if (!request.isCurrent()) return;
      const incomplete = outcome.status !== 'completed';
      setResults((prev) => {
        if (!request.isCurrent() || prev?.query !== q) return prev;
        const updated = { ...prev, search_incomplete: incomplete };
        if (!incomplete) trackSearch(q, updated.total_all || 0);
        return updated;
      });
      if (outcome.status === 'partial') {
        setSearchError('Часть разделов временно недоступна. Показаны только полученные результаты. Повторите поиск.');
      } else if (outcome.status === 'failed') {
        setSearchError('Не удалось выполнить поиск. Повторите попытку.');
      }
      setSearchTime(Math.round(performance.now() - t0));
      addToRecent(q);
    } catch (error) {
      if (!request.isCurrent()) return;
      setSearchError(pickSafeRussianBackendMessage(
        error.response?.data?.error ?? error.response?.data?.message,
        pickHttpStatusRussianFallback(error.response?.status)
      ));
      setResults((prev) => (prev?.query === q ? { ...prev, search_incomplete: true } : null));
    } finally {
      if (request.isCurrent()) {
        setLoading(false);
        setSearchingMore(false);
        setSearchingMoreLabel('');
      }
    }
  }, [addToRecent, searchLifecycle]);

  // ✅ ОПТИМИЗАЦИЯ: Поиск ТОЛЬКО по Enter (до 3М+ товаров — auto-search перегрузит систему)
  // Предыдущие результаты остаются видимыми пока не придут новые
  const handleQueryChange = useCallback((value) => {
    searchLifecycle.cancel();
    setQuery(value);
    setResults(null);
    setSearchError(null);
    setSearchTime(null);
    setLoading(false);
    setSearchingMore(false);
    setSearchingMoreLabel('');
    setSelectedIndex(0);
  }, [searchLifecycle]);

  const stopSearch = useCallback(() => {
    searchLifecycle.cancel();
    setLoading(false);
    setSearchingMore(false);
    setSearchingMoreLabel('');
    setResults((prev) => (prev ? { ...prev, search_incomplete: true } : prev));
    setSearchError('Поиск остановлен. Результаты могут быть неполными. Повторите поиск, чтобы продолжить.');
  }, [searchLifecycle]);

  // ────────────────────────────
  // Открытие / закрытие
  // ✅ ИСПРАВЛЕНО: Автоматический поиск только для запросов >= 2 символов (сканер штрихкодов)
  // ✅ ИСПРАВЛЕНО: Быстрая установка фокуса для мгновенного открытия окна
  // ────────────────────────────
  const handleOpen = useCallback((initialQuery = '') => {
    searchLifecycle.cancel();
    setLoading(false);
    setSearchingMore(false);
    setSearchingMoreLabel('');
    setOpen(true);
    setQuery(initialQuery);
    setResults(null);
    setSearchError(null);
    setSelectedIndex(0);
    if (initialQuery && initialQuery.trim().length >= 2) {
      performSearch(initialQuery);
    }
  }, [performSearch, searchLifecycle]);

  const handleClose = useCallback(() => {
    // ✅ ОПТИМИЗАЦИЯ: Очистка всех таймеров и отмена запросов
    searchLifecycle.cancel();
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
    // Сбрасываем флаг открытия
    isOpeningRef.current = false;
    // Батчинг: обновляем все состояния одновременно
    setOpen(false);
    setQuery('');
    setResults(null);
    setSearchError(null);
    setSelectedIndex(0);
    setLoading(false);
    setSearchingMore(false);
    setSearchingMoreLabel('');
  }, [searchLifecycle]);

  // ────────────────────────────
  // Клавиатурные сочетания (режим hotkey)
  // ────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K / Cmd+K — e.code=KeyK, иначе на русской раскладке e.key=«л» и хоткей мёртв
      const isSearchHotkey = (e.ctrlKey || e.metaKey)
        && !e.altKey
        && (e.code === 'KeyK' || String(e.key || '').toLowerCase() === 'k');
      if (isSearchHotkey) {
        if (activationModeRef.current !== GLOBAL_SEARCH_ACTIVATION.HOTKEY && !open) {
          // В режиме «Ввод» хоткей не открывает — только закрывает, если уже открыто
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (open) {
          handleClose();
        } else {
          handleOpen();
        }
        return;
      }

      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, handleOpen, handleClose]);

  // ────────────────────────────
  // Body-scroll-lock пока модалка открыта
  // (раньше делал AntD <Modal>, теперь — нативная DS-модалка)
  // ────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  // ────────────────────────────
  // Кастомное событие открытия (от Header)
  // ────────────────────────────
  useEffect(() => {
    const handleCustomOpen = (e) => {
      handleOpen(e.detail?.query || '');
    };
    window.addEventListener(GLOBAL_SEARCH_EVENT, handleCustomOpen);
    return () => window.removeEventListener(GLOBAL_SEARCH_EVENT, handleCustomOpen);
  }, [handleOpen]);

  // Focus once after the modal DOM mounts, before the user can move to a result.
  // Delayed autofocus must not steal focus from a card during later search waves.
  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const input = inputRef.current;
    input.focus();
    try {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    } catch {
      /* Search input selection is not supported in every browser. */
    }
  }, [open]);

  // ────────────────────────────
  // Глобальный ввод (режим type): сканер штрихкодов + клавиатурный поиск
  // keydown + preventDefault — иначе первая буква попадает и в seed, и в input (дубль)
  // ────────────────────────────
  useEffect(() => {
    if (open || isOpeningRef.current) return undefined;
    if (activationMode !== GLOBAL_SEARCH_ACTIVATION.TYPE) return undefined;

    const handleGlobalKeyDown = (e) => {
      if (activationModeRef.current !== GLOBAL_SEARCH_ACTIVATION.TYPE) return;
      if (e.isComposing || e.keyCode === 229) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1) return;

      const now = Date.now();
      const firstChar = scanBufferRef.current.length === 0 ? e.key : null;

      scanBufferRef.current.push(e.key);
      scanTimestampsRef.current.push(now);

      if (firstChar !== null) {
        e.preventDefault();
        e.stopPropagation();
        isOpeningRef.current = true;
        handleOpen(firstChar);
        scanBufferRef.current = [];
        scanTimestampsRef.current = [];
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
          scanTimeoutRef.current = null;
        }
        setTimeout(() => {
          isOpeningRef.current = false;
        }, 150);
        return;
      }

      // Сканер: пачка символов до открытия — копим и открываем целиком
      e.preventDefault();
      e.stopPropagation();
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);

      scanTimeoutRef.current = setTimeout(() => {
        const chars = scanBufferRef.current;
        const text = chars.join('');

        if (text.length >= 2) {
          isOpeningRef.current = true;
          handleOpen(text);
          setTimeout(() => {
            isOpeningRef.current = false;
          }, 150);
        }

        scanBufferRef.current = [];
        scanTimestampsRef.current = [];
      }, 250);
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      scanBufferRef.current = [];
      scanTimestampsRef.current = [];
      isOpeningRef.current = false;
    };
  }, [open, handleOpen, activationMode]);

  // ────────────────────────────
  // Навигация по результатам
  // ────────────────────────────
  const allItems = useMemo(() => {
    if (!results) return [];
    const items = [];
    // Бренды первыми — фильтр, не текстовый поиск
    results.brands?.forEach((b) => items.push({ type: 'brand', data: b }));
    results.our_products?.forEach(p => items.push({ type: 'our', data: p }));
    results.supplier_products?.forEach(p => items.push({ type: 'supplier', data: p }));
    (results.result_groups || []).forEach(group => {
      (group.items || []).forEach(item => items.push({ type: 'entity', group, data: item }));
    });
    return items;
  }, [results]);

  // ────────────────────────────
  // Клик по результату
  // ✅ ИСПРАВЛЕНО: Перемещено ПЕРЕД handleKeyNavigation для устранения TDZ ошибки
  // ────────────────────────────
  const openBrandFilter = useCallback((brand) => {
    const name = String(brand?.name || '').trim();
    if (!name) return;
    const params = new URLSearchParams();
    params.set('brand_name', name);
    if (brand?.id) params.set('brand_id', String(brand.id));
    params.set('open_view', 'our');
    navigate(`/products?${params.toString()}`);
    handleClose();
  }, [navigate, handleClose]);

  const handleItemClick = useCallback((item) => {
    const searchQ = query?.trim() || '';

    if (item.type === 'brand') {
      openBrandFilter(item.data);
      return;
    }

    if (item.type === 'our') {
      // Открываем просмотр товара (не режим редактирования)
      const params = new URLSearchParams();
      if (searchQ) params.set('search', searchQ);
      if (item.data?.id) params.set('product_id', String(item.data.id));
      params.set('open_view', 'our');
      navigate(`/products?${params.toString()}`);
    } else if (item.type === 'supplier') {
      // Для товара поставщика всегда переключаемся в контекст поставщика
      const supplierId = item.data.supplier_id;
      const externalId = item.data.external_id || item.data.external_code;
      const params = new URLSearchParams();
      if (supplierId) params.set('supplier_id', String(supplierId));
      if (searchQ) {
        params.set('search', searchQ);
      } else if (externalId) {
        params.set('search', String(externalId));
      }
      if (externalId) {
        params.set('highlight', String(externalId));
        params.set('external_id', String(externalId));
      }
      params.set('open_view', 'supplier');
      const preferredWarehouse = item.data.preferred_warehouse_id || item.data.preferred_warehouse_external_id || item.data.warehouse_id;
      if (preferredWarehouse) {
        params.set('warehouse_id', String(preferredWarehouse));
      }
      navigate(`/products?${params.toString()}`);
    } else if (item.type === 'entity') {
      const href = item.data?.href || item.group?.href || '/';
      navigate(href);
    }
    handleClose();
  }, [navigate, handleClose, query, openBrandFilter]);

  const handleKeyNavigation = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      // Enter при наличии результатов и навигации → открыть выбранный элемент
      if (allItems.length > 0 && selectedIndex > 0) {
        const item = allItems[selectedIndex];
        if (item) {
          handleItemClick(item);
          return;
        }
      }
      // Иначе — запуск поиска (основное поведение)
      if (query && query.trim().length >= 2) {
        performSearch(query);
      }
      return;
    }

    if (!allItems.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    }
  }, [allItems, selectedIndex, query, performSearch, handleItemClick]);

  // ────────────────────────────
  // Группировка поставщиков
  // ────────────────────────────
  const supplierGroups = useMemo(() => {
    if (!results?.supplier_products?.length) return [];
    const groups = {};
    for (const sp of results.supplier_products) {
      const key = sp.supplier_id;
      if (!groups[key]) {
        groups[key] = {
          supplier_id: sp.supplier_id,
          supplier_name: sp.supplier_name || 'Поставщик',
          supplier_code: sp.supplier_code,
          products: [],
        };
      }
      groups[key].products.push(sp);
    }
    return Object.values(groups);
  }, [results]);



  // ────────────────────────────
  // Рендер содержимого
  // ────────────────────────────
  const hasResults = results && (
    results.total_our > 0 || results.total_supplier > 0 ||
    results.total_categories > 0 || results.total_brands > 0 ||
    (results.result_groups || []).some((group) => (group.total || group.items?.length || 0) > 0)
  );
  const noResults = results && !results.search_incomplete && !hasResults && !loading && !searchingMore && !searchError;
  const totalFound = results ? (
    results.total_all ||
    (results.total_our || 0) +
    (results.total_supplier || 0) +
    (results.total_categories || 0) +
    (results.total_brands || 0) +
    (results.result_groups || []).reduce((sum, group) => sum + (group.total || group.items?.length || 0), 0)
  ) : 0;
  let globalIdx = 0;

  if (!open) return null;

  return (
    <div
      className="mb-modal-backdrop gs-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      role="presentation"
    >
      <div
        className="mb-modal mb-modal--wide gs-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Глобальный поиск по платформе"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* ── Поисковая строка ── */}
        <div className="gs-search-bar">
          <Search className="gs-search-bar__icon" size={18} aria-hidden />
          <input
            ref={inputRef}
            type="search"
            className="gs-search-bar__input mb-input"
            placeholder="Введите запрос и нажмите Enter"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={handleKeyNavigation}
            autoComplete="off"
            spellCheck={false}
            aria-label="Поисковый запрос"
          />
          <div className="gs-search-bar__actions">
            {loading && (
              <span className="mb-spin mb-spin--sm" aria-busy="true" aria-label="Идёт поиск" />
            )}
            {query && !loading && (
              <>
                <button
                  type="button"
                  className="mb-btn mb-btn--primary mb-btn--sm gs-search-bar__submit"
                  onClick={() => {
                    if (query && query.trim().length >= 2) {
                      performSearch(query);
                    }
                  }}
                >
                  <Search size={14} aria-hidden />
                  <span>Найти</span>
                </button>
                <button
                  type="button"
                  className="mb-btn mb-btn--ghost mb-btn--sm mb-btn--icon"
                  onClick={() => {
                    handleQueryChange('');
                    inputRef.current?.focus();
                  }}
                  aria-label="Очистить запрос"
                  title="Очистить"
                >
                  <X size={14} aria-hidden />
                </button>
              </>
            )}
            <kbd className="gs-search-bar__kbd">ESC</kbd>
          </div>
        </div>

        {searchError && (
          <div className="gs-search-error-wrap" role="alert">
            <div className="gs-search-error-alert mb-banner mb-banner--err">
              <AlertCircle size={16} aria-hidden style={{ color: 'var(--err)', flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{searchError}</span>
              <button
                type="button"
                className="mb-btn mb-btn--ghost mb-btn--sm mb-btn--icon"
                onClick={() => setSearchError(null)}
                aria-label="Закрыть сообщение об ошибке"
                title="Закрыть"
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          </div>
        )}

      {/* ── Контент результатов ── */}
      <div className="gs-results-container">
        {/* Штрихкод-бейдж */}
        {results?.is_barcode && (
          <div className="gs-barcode-badge">
            <ScanBarcode size={14} aria-hidden /> Обнаружен штрихкод
          </div>
        )}

        {/* ── Саммари результатов ── */}
        {hasResults && (
          <div className="gs-summary-bar">
            <div className="gs-summary-bar__left">
              <span className="gs-summary-bar__query">
                Результаты по запросу «<strong>{results.query || query}</strong>»
              </span>
              {searchingMore && (
                <span className="gs-summary-bar__progress">
                  <span className="gs-summary-bar__progress-dot" aria-hidden />
                  {searchingMoreLabel || 'ищем дальше…'}
                </span>
              )}
            </div>
            <div className="gs-summary-bar__right">
              <span className="gs-summary-bar__count">{totalFound} найдено</span>
              {searchTime !== null && (
                <span className="gs-summary-bar__time">{searchTime} мс</span>
              )}
              {searchingMore && (
                <button
                  type="button"
                  className="mb-btn mb-btn--outline mb-btn--sm"
                  style={{ marginLeft: 8 }}
                  onClick={stopSearch}
                >
                  Остановить
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Пустое состояние: ожидание ввода ── */}
        {!results && !loading && (
          <div className="gs-empty-state">
            {recentSearches.length > 0 && (
              <div className="gs-recent-section">
                <div className="gs-section-header">
                  <History className="gs-section-header__icon" size={16} aria-hidden />
                  <span className="gs-section-header__text">Недавние поиски</span>
                </div>
                <div className="gs-recent-list">
                  {recentSearches.map((q, i) => (
                    <button
                      type="button"
                      key={i}
                      className="gs-recent-item"
                      onClick={() => { setQuery(q); performSearch(q); }}
                    >
                      <History size={14} aria-hidden />
                      <span>{q}</span>
                      <ArrowRight className="gs-recent-item__arrow" size={14} aria-hidden />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!recentSearches.length && (
              <div className="gs-empty-hint">
                <div className="gs-empty-hint__icon-wrap">
                  <Search size={32} aria-hidden />
                </div>
                <div className="gs-empty-hint__title">Быстрый поиск</div>
                <div className="gs-empty-hint__sub">
                  Товары, штрихкоды, артикулы, бренды, поставщики
                </div>
                <div className="gs-empty-hint__enter">
                  Введите запрос и нажмите <kbd>Enter</kbd>
                </div>
              </div>
            )}

            <div className="gs-divider" />
            <div className="gs-quick-actions">
              <span className="gs-quick-actions__label">Быстрый доступ</span>
              <div className="gs-quick-actions__list">
                <button type="button" className="gs-quick-action" onClick={() => { navigate('/products'); handleClose(); }}>
                  <Inbox size={14} aria-hidden /> Все товары
                </button>
                <button type="button" className="gs-quick-action" onClick={() => { navigate('/suppliers'); handleClose(); }}>
                  <Store size={14} aria-hidden /> Поставщики
                </button>
                <button type="button" className="gs-quick-action" onClick={() => { navigate('/modules/orders'); handleClose(); }}>
                  <ShoppingCart size={14} aria-hidden /> Заказы
                </button>
                <button
                  type="button"
                  className="gs-quick-action"
                  onClick={() => {
                    navigate(canOpenAdminSearchSettings ? '/modules/search' : '/settings?tab=user_search_settings');
                    handleClose();
                  }}
                >
                  <Settings size={14} aria-hidden /> SEARCH
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Загрузка (только пока нет промежуточных результатов) ── */}
        {loading && !hasResults && (
          <div className="gs-loading" role="status" aria-live="polite">
            <span className="mb-spin mb-spin--lg" aria-hidden />
            <span className="gs-loading__text">Поиск по базе...</span>
          </div>
        )}

        {searchingMore && !hasResults && (
          <div className="gs-loading" role="status" aria-live="polite">
            <span className="mb-spin mb-spin--lg" aria-hidden />
            <span className="gs-loading__text">Ищем совпадения…</span>
            <button
              type="button"
              className="mb-btn mb-btn--outline mb-btn--sm"
              style={{ marginTop: 12 }}
              onClick={stopSearch}
            >
              Остановить поиск
            </button>
          </div>
        )}

        {/* ── Нет результатов ── */}
        {noResults && (
          <div className="gs-no-results">
            <div className="gs-no-results__icon" aria-hidden>
              <Search size={32} />
            </div>
            <div className="gs-no-results__text">
              По запросу «<strong>{results.query || query}</strong>» ничего не найдено
            </div>
            <div className="gs-no-results__hint">
              Попробуйте изменить запрос, проверить написание или добавить товар
            </div>
            <div className="gs-no-results__actions">
              <button
                type="button"
                className="mb-btn mb-btn--primary mb-btn--sm"
                onClick={() => {
                  navigate(`/products/new?name=${encodeURIComponent(query)}`);
                  handleClose();
                }}
              >
                <Plus size={14} aria-hidden />
                <span>Добавить товар</span>
              </button>
              {results.is_barcode && (
                <button
                  type="button"
                  className="mb-btn mb-btn--outline mb-btn--sm"
                  onClick={() => {
                    navigate(`/products/new?barcode=${encodeURIComponent(query)}`);
                    handleClose();
                  }}
                >
                  <ScanBarcode size={14} aria-hidden />
                  <span>Добавить по штрихкоду</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── БРЕНДЫ (сверху): клик = фильтр на /products, не текстовый поиск ── */}
        {hasResults && results.brands?.length > 0 && (
          <div className="gs-section gs-section--brands">
            <div className="gs-section-header">
              <TagIcon className="gs-section-header__icon gs-section-header__icon--brands" size={16} aria-hidden />
              <span className="gs-section-header__text">Бренды</span>
              <span className="gs-count-badge" aria-label={`Найдено ${results.total_brands}`}>
                {results.total_brands > 99 ? '99+' : results.total_brands}
              </span>
              <span className="gs-section-header__hint">фильтр</span>
            </div>
            <div className="gs-brands-list">
              {results.brands.map((brand) => {
                const idx = globalIdx++;
                return (
                  <button
                    key={brand.id || brand.name}
                    type="button"
                    className={`gs-brand-item ${selectedIndex === idx ? 'gs-result-selected' : ''}`}
                    title={`Открыть товары с фильтром по бренду «${brand.name}»`}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    onClick={() => openBrandFilter(brand)}
                  >
                    <TagIcon className="gs-brand-item__icon" size={14} aria-hidden />
                    <span className="gs-brand-item__name">{highlightMatch(brand.name, query)}</span>
                    {brand.matched_alias && (
                      <span className="gs-brand-item__alias">алиас: {brand.matched_alias}</span>
                    )}
                    <span className="gs-brand-item__action">Фильтр</span>
                    {brand.products_count != null && (
                      <span className="gs-brand-item__count">{brand.products_count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── НАШИ ТОВАРЫ ── */}
        {hasResults && results.total_our > 0 && (
          <div className="gs-section gs-section--our">
            <div className="gs-section-header">
              <Inbox className="gs-section-header__icon gs-section-header__icon--our" size={16} aria-hidden />
              <span className="gs-section-header__text">Наши товары</span>
              <span className="gs-count-badge" aria-label={`Найдено ${results.total_our}`}>
                {results.total_our > 999 ? '999+' : results.total_our}
              </span>
            </div>
            <div className="gs-our-products-list">
              {results.our_products.map(p => {
                const idx = globalIdx++;
                return (
                  <OurProductItem
                    key={`our-${p.id}`}
                    product={p}
                    globalIdx={idx}
                    isSelected={selectedIndex === idx}
                    onSelect={() => setSelectedIndex(idx)}
                    onClick={() => handleItemClick({ type: 'our', data: p })}
                    searchQuery={query}
                  />
                );
              })}
            </div>
            <button
              className="gs-section-more"
              onClick={() => {
                navigate(`/products?search=${encodeURIComponent(query)}`);
                handleClose();
              }}
            >
              <span>Показать все наши товары по запросу</span>
              <ArrowRight size={14} aria-hidden />
            </button>
          </div>
        )}

        {/* ── ТОВАРЫ ПОСТАВЩИКОВ (по группам) ── */}
        {hasResults && supplierGroups.length > 0 && (
          <div className="gs-section gs-section--supplier">
            <div className="gs-section-header">
              <Store className="gs-section-header__icon gs-section-header__icon--supplier" size={16} aria-hidden />
              <span className="gs-section-header__text">Товары поставщиков</span>
              <span className="gs-count-badge" aria-label={`Найдено ${results.total_supplier}`}>
                {results.total_supplier > 999 ? '999+' : results.total_supplier}
              </span>
            </div>
            {supplierGroups.map(group => (
              <div key={group.supplier_id} className="gs-supplier-group">
                <div className="gs-supplier-group__header">
                  <div className="gs-supplier-group__dot" />
                  <span className="gs-supplier-group__name">{group.supplier_name}</span>
                  {group.supplier_code && (
                    <span className="mb-badge mb-badge--neutral gs-supplier-group__code-tag">
                      {group.supplier_code}
                    </span>
                  )}
                  <span className="gs-supplier-group__count">
                    {group.products.length} {group.products.length === 1 ? 'товар' : group.products.length < 5 ? 'товара' : 'товаров'}
                  </span>
                </div>
                <div className="gs-supplier-grid">
                  {group.products.map(sp => {
                    const idx = globalIdx++;
                    return (
                      <SupplierProductItem
                        key={`sup-${sp.id}`}
                        product={sp}
                        globalIdx={idx}
                        isSelected={selectedIndex === idx}
                        onSelect={() => setSelectedIndex(idx)}
                        onClick={() => handleItemClick({ type: 'supplier', data: sp })}
                        searchQuery={query}
                      />
                    );
                  })}
                </div>
                {/* Кнопка "Все товары этого поставщика" — переключает страницу на конкретного поставщика */}
                <button
                  className="gs-section-more gs-section-more--supplier"
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set('supplier_id', String(group.supplier_id));
                    if (query) params.set('search', query.trim());
                    navigate(`/products?${params.toString()}`);
                    handleClose();
                  }}
                >
                  <span>Все товары поставщика «{group.supplier_name}»</span>
                  <ArrowRight size={14} aria-hidden />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── КАТЕГОРИИ ── */}
        {hasResults && results.categories?.length > 0 && (
          <div className="gs-section gs-section--categories">
            <div className="gs-section-header">
              <Grid3x3 className="gs-section-header__icon gs-section-header__icon--categories" size={16} aria-hidden />
              <span className="gs-section-header__text">Категории</span>
              <span className="gs-count-badge" aria-label={`Найдено ${results.total_categories}`}>
                {results.total_categories > 99 ? '99+' : results.total_categories}
              </span>
            </div>
            <div className="gs-categories-list">
              {results.categories.map((cat) => (
                <button
                  key={cat.id || cat.name}
                  className="gs-category-item"
                  onClick={() => {
                    navigate(`/products?category_id=${cat.id}&search=${encodeURIComponent(query)}`);
                    handleClose();
                  }}
                >
                  <Grid3x3 className="gs-category-item__icon" size={14} aria-hidden />
                  <span className="gs-category-item__name">{cat.name}</span>
                  {cat.products_count != null && (
                    <span className="gs-category-item__count">{cat.products_count}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── НАСТРАИВАЕМЫЕ ГРУППЫ SEARCH ── */}
        {hasResults && (results.result_groups || []).map((group) => (
          <div key={group.key} className="gs-section gs-section--entities">
            <div className="gs-section-header">
              <Search className="gs-section-header__icon" size={16} aria-hidden />
              <span className="gs-section-header__text">{group.title}</span>
              <span className="gs-count-badge" aria-label={`Найдено ${group.total || group.items?.length || 0}`}>
                {(group.total || group.items?.length || 0) > 99 ? '99+' : (group.total || group.items?.length || 0)}
              </span>
            </div>
            <div className="gs-categories-list">
              {(group.items || []).map((item) => {
                const idx = globalIdx++;
                return (
                  <button
                    key={`${group.key}-${item.id || item.href || item.title}`}
                    className={`gs-category-item ${selectedIndex === idx ? 'gs-result-selected' : ''}`}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    onClick={() => handleItemClick({ type: 'entity', group, data: item })}
                  >
                    <Search className="gs-category-item__icon" size={14} aria-hidden />
                    <span className="gs-category-item__name">{highlightMatch(item.title || item.name || item.number || 'Результат', query)}</span>
                    {item.subtitle && <span className="gs-category-item__count">{item.subtitle}</span>}
                  </button>
                );
              })}
            </div>
            {group.href && (
              <button
                className="gs-section-more"
                onClick={() => {
                  navigate(`${group.href}?search=${encodeURIComponent(query)}`);
                  handleClose();
                }}
              >
                <span>Открыть раздел «{group.title}»</span>
                <ArrowRight size={14} aria-hidden />
              </button>
            )}
          </div>
        ))}

        {/* ── Подсказки по клавишам ── */}
        <div className="gs-footer-hints">
          <span><kbd>Enter</kbd> поиск</span>
          <span><kbd>↑↓</kbd> навигация</span>
          <span><kbd>Esc</kbd> закрыть</span>
          <span className="gs-footer-hints__right"><kbd>Ctrl</kbd>+<kbd>K</kbd> открыть поиск</span>
        </div>
      </div>
      </div>
    </div>
  );
}
